import React, { useCallback, useEffect, useRef } from "react";
import { useTheme } from "../contexts/ThemeContext";

/**
 * Animated constellation behind the landing hero: drifting points joined when
 * they come close, a few slow wireframe polygons, and a breathing centre glow.
 *
 * Three things it does differently from the usual copy of this effect:
 *  - colours are read from the --u-* tokens, so it follows the active theme
 *    instead of hard-coding a palette;
 *  - the pointer is tracked on window rather than through an overlay element,
 *    which would sit on top of the hero and swallow clicks on the buttons;
 *  - links are found through a spatial grid, so cost grows with the number of
 *    points rather than with its square.
 */

const LINK_DISTANCE = 120;
const POINTER_RADIUS = 150;

/** "6 111 210" (a token triplet) -> "rgba(6, 111, 210, a)" */
const rgba = (triplet, alpha) => `rgba(${triplet.trim().split(/\s+/).join(", ")}, ${alpha})`;

const readPalette = () => {
    const s = getComputedStyle(document.documentElement);
    const token = (name, fallback) => s.getPropertyValue(name).trim() || fallback;
    return {
        accent: token("--u-accent", "6 111 210"),
        light: token("--u-brand-400", "90 162 222"),
        deep: token("--u-brand-600", "6 111 210"),
    };
};

const LandingBackdrop = () => {
    const hostRef = useRef(null);
    const canvasRef = useRef(null);
    const pointsRef = useRef([]);
    const shapesRef = useRef([]);
    const pointerRef = useRef({ x: -9999, y: -9999 });
    const rafRef = useRef(null);
    const paletteRef = useRef(null);
    const { theme } = useTheme();

    const seed = useCallback((w, h) => {
        const compact = w < 768;
        const count = compact ? 48 : 96;
        const palette = paletteRef.current;
        const tints = [palette.accent, palette.light, palette.deep];

        pointsRef.current = Array.from({ length: count }, (_, i) => ({
            x: Math.random() * w,
            y: Math.random() * h,
            vx: (Math.random() - 0.5) * 0.24,
            vy: (Math.random() - 0.5) * 0.24,
            r: Math.random() * 1.6 + 0.6,
            a: Math.random() * 0.4 + 0.25,
            tint: tints[i % tints.length],
        }));

        shapesRef.current = Array.from({ length: compact ? 3 : 6 }, () => ({
            x: Math.random() * w,
            y: Math.random() * h,
            size: 22 + Math.random() * 38,
            sides: [3, 4, 6][Math.floor(Math.random() * 3)],
            rot: Math.random() * Math.PI * 2,
            spin: (Math.random() - 0.5) * 0.006,
            phase: Math.random() * Math.PI * 2,
            bobSpeed: 0.3 + Math.random() * 0.4,
            bob: 12 + Math.random() * 16,
            a: 0.07 + Math.random() * 0.09,
        }));
    }, []);

    useEffect(() => {
        const host = hostRef.current;
        const canvas = canvasRef.current;
        if (!host || !canvas) return;

        const ctx = canvas.getContext("2d");
        const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        paletteRef.current = readPalette();

        let w = 0;
        let h = 0;

        const resize = () => {
            const rect = host.getBoundingClientRect();
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            w = Math.max(1, Math.round(rect.width));
            h = Math.max(1, Math.round(rect.height));
            canvas.width = w * dpr;
            canvas.height = h * dpr;
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            seed(w, h);
        };

        resize();

        const observer = new ResizeObserver(resize);
        observer.observe(host);

        const onPointerMove = (e) => {
            const rect = host.getBoundingClientRect();
            pointerRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        };
        const onPointerLeave = () => {
            pointerRef.current = { x: -9999, y: -9999 };
        };
        window.addEventListener("mousemove", onPointerMove, { passive: true });
        window.addEventListener("mouseout", onPointerLeave, { passive: true });

        let t = 0;

        const draw = () => {
            const palette = paletteRef.current;
            ctx.clearRect(0, 0, w, h);

            /* Centre glow, breathing slowly */
            const radius = Math.min(w, h) * 0.55 + Math.sin(t * 0.5) * 18;
            const glow = ctx.createRadialGradient(w * 0.5, h * 0.42, 0, w * 0.5, h * 0.42, radius);
            glow.addColorStop(0, rgba(palette.accent, 0.09));
            glow.addColorStop(1, rgba(palette.accent, 0));
            ctx.fillStyle = glow;
            ctx.fillRect(0, 0, w, h);

            /* Wireframe polygons */
            ctx.lineWidth = 1;
            shapesRef.current.forEach((s) => {
                if (!reduced) s.rot += s.spin;
                const bobY = reduced ? 0 : Math.sin(t * s.bobSpeed + s.phase) * s.bob;
                ctx.save();
                ctx.translate(s.x, s.y + bobY);
                ctx.rotate(s.rot);
                ctx.beginPath();
                for (let i = 0; i <= s.sides; i++) {
                    const angle = (i / s.sides) * Math.PI * 2;
                    const px = Math.cos(angle) * s.size;
                    const py = Math.sin(angle) * s.size;
                    if (i === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }
                ctx.closePath();
                ctx.strokeStyle = rgba(palette.light, s.a);
                ctx.stroke();
                ctx.restore();
            });

            const points = pointsRef.current;
            const { x: mx, y: my } = pointerRef.current;

            points.forEach((p) => {
                if (!reduced) {
                    p.x += p.vx;
                    p.y += p.vy;
                    if (p.x < -8) p.x = w + 8;
                    if (p.x > w + 8) p.x = -8;
                    if (p.y < -8) p.y = h + 8;
                    if (p.y > h + 8) p.y = -8;

                    /* Nudge away from the pointer, so the field reacts to it */
                    const dx = p.x - mx;
                    const dy = p.y - my;
                    const d = Math.hypot(dx, dy);
                    if (d > 0 && d < POINTER_RADIUS) {
                        const push = ((POINTER_RADIUS - d) / POINTER_RADIUS) * 1.4;
                        p.x += (dx / d) * push;
                        p.y += (dy / d) * push;
                    }
                }

                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fillStyle = rgba(p.tint, p.a);
                ctx.fill();
            });

            /* Links, resolved through a grid of LINK_DISTANCE-sized cells so each
               point only tests its own cell and the neighbours it could reach. */
            const cols = Math.max(1, Math.ceil(w / LINK_DISTANCE));
            const cells = new Map();
            points.forEach((p, i) => {
                const key =
                    Math.floor(Math.max(0, p.y) / LINK_DISTANCE) * cols +
                    Math.floor(Math.max(0, p.x) / LINK_DISTANCE);
                const bucket = cells.get(key);
                if (bucket) bucket.push(i);
                else cells.set(key, [i]);
            });

            ctx.lineWidth = 0.6;
            ctx.strokeStyle = rgba(palette.accent, 1);
            points.forEach((a, i) => {
                const cx = Math.floor(Math.max(0, a.x) / LINK_DISTANCE);
                const cy = Math.floor(Math.max(0, a.y) / LINK_DISTANCE);
                for (let oy = 0; oy <= 1; oy++) {
                    for (let ox = -1; ox <= 1; ox++) {
                        if (oy === 0 && ox < 0) continue;
                        const bucket = cells.get((cy + oy) * cols + (cx + ox));
                        if (!bucket) continue;
                        bucket.forEach((j) => {
                            if (j <= i) return;
                            const b = points[j];
                            const d = Math.hypot(a.x - b.x, a.y - b.y);
                            if (d >= LINK_DISTANCE) return;
                            ctx.globalAlpha = (1 - d / LINK_DISTANCE) * 0.22;
                            ctx.beginPath();
                            ctx.moveTo(a.x, a.y);
                            ctx.lineTo(b.x, b.y);
                            ctx.stroke();
                        });
                    }
                }
            });
            ctx.globalAlpha = 1;

            if (reduced) return; // one static frame is enough
            t += 0.016;
            rafRef.current = requestAnimationFrame(draw);
        };

        draw();

        return () => {
            observer.disconnect();
            window.removeEventListener("mousemove", onPointerMove);
            window.removeEventListener("mouseout", onPointerLeave);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
        /* theme is a dependency: the palette is read from the tokens, so the
           field has to be rebuilt when they change. */
    }, [seed, theme]);

    return (
        <div ref={hostRef} className="landing-canvas" aria-hidden="true">
            <canvas ref={canvasRef} />
        </div>
    );
};

export default LandingBackdrop;
